import { useState, useRef, useCallback, useEffect } from 'react';

const useVoiceCommand = (options = {}) => {
  const {
    onFinalTranscript,
    isSessionActiveRef,
    suppressFinalizeRef,
  } = options;

  const onFinalTranscriptRef = useRef(onFinalTranscript);
  onFinalTranscriptRef.current = onFinalTranscript;

  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);
  const [endedWithoutTranscript, setEndedWithoutTranscript] = useState(false);
  const recognitionRef = useRef(null);
  const latestTranscriptRef = useRef('');
  const mountedRef = useRef(true);

  const reset = useCallback(() => {
    setTranscript('');
    setError(null);
    setEndedWithoutTranscript(false);
    latestTranscriptRef.current = '';
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setError('Speech recognition is not supported in this browser');
      return undefined;
    }

    setTranscript('');
    setError(null);
    setIsListening(false);

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
      setEndedWithoutTranscript(false);
      setTranscript('');
      latestTranscriptRef.current = '';
    };

    recognition.onresult = (event) => {
      let fullTranscript = '';

      for (let i = 0; i < event.results.length; i += 1) {
        fullTranscript += event.results[i][0].transcript;
      }

      const fullTrimmed = fullTranscript.trim();
      latestTranscriptRef.current = fullTrimmed;
      setTranscript(fullTrimmed);
    };

    recognition.onerror = (event) => {
      setError(event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);

      if (!mountedRef.current) {
        return;
      }

      if (suppressFinalizeRef?.current) {
        suppressFinalizeRef.current = false;
        return;
      }

      if (isSessionActiveRef && !isSessionActiveRef.current) {
        return;
      }

      const spoken = String(latestTranscriptRef.current || '').trim();
      if (spoken && onFinalTranscriptRef.current) {
        onFinalTranscriptRef.current(spoken);
      } else {
        setEndedWithoutTranscript(true);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      mountedRef.current = false;
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch {
          /* not running */
        }
        recognitionRef.current = null;
      }
    };
  }, [isSessionActiveRef, suppressFinalizeRef]);

  const startListening = useCallback(() => {
    if (recognitionRef.current && !isListening) {
      try {
        recognitionRef.current.start();
      } catch (err) {
        setError(err.message);
      }
    }
  }, [isListening]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current && isListening) {
      try {
        recognitionRef.current.stop();
      } catch {
        /* not running */
      }
      setIsListening(false);
    }
  }, [isListening]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  const isSupported = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  return {
    isListening,
    transcript,
    error,
    endedWithoutTranscript,
    startListening,
    stopListening,
    toggleListening,
    reset,
    isSupported
  };
};

export default useVoiceCommand;
